<template>
    <div class="flex h-full flex-col font-inter">
        <div class="space-between flex w-full pt-4">
            <div class="flex-1">
                <h2 class="font-bold">Request document</h2>
            </div>
        </div>
        <hr class="my-4" />
        <div class="grid grid-cols-1 gap-y-2">
            <span>Recipients</span>
            <MultiSelect
                v-model="selectedClients"
                class="min-w-full"
                display="chip"
                optionLabel="email"
                :options="possibleClients"
                placeholder="Who should complete this task"
                :pt="genericMultiselectPtOptions"
            >
                <template #option="slotProps">
                    <div class="ml-2">
                        {{ slotProps.option.displayOption }}
                    </div>
                </template>
                <template #headercheckboxicon><!-- empty slot to avoid PV default --></template>
                <template #itemcheckboxicon><!-- empty slot to avoid PV default --></template>
            </MultiSelect>
            <hr class="my-4" />
            <div
                v-for="(value, index) in documents"
                :key="index"
                class="flex flex-col pb-4"
            >
                <span class="mb-2">Document {{ index + 1 }}</span>
                <div class="flex items-center gap-2">
                    <input
                        v-model="documents[index]"
                        class="form-control flex-1 p-3"
                        required
                        type="text"
                    />
                    <FontAwesomeIcon
                        v-if="index !== 0"
                        class="cursor-pointer text-ds-red-600"
                        icon="far fa-circle-minus"
                        @click="removeDocument(index)"
                    />
                </div>
            </div>

            <div class="mt-2 flex justify-end">
                <DsButton
                    leading="far fa-plus-circle"
                    size="sm"
                    variant="tertiary"
                    @click="incrementDocCount"
                >
                    <span> Request another document</span>
                </DsButton>
            </div>
        </div>
        <hr class="my-4" />
        <div class="mt-2 flex flex-row items-end justify-end gap-1 text-sm">
            <DsButton
                class="mr-2 px-6 py-4"
                size="md"
                variant="secondary"
                @click="emit('close')"
            >
                Cancel
            </DsButton>
            <DsButton
                class="px-6 py-4"
                :disabled="sendRequestDisabled"
                size="md"
                variant="primary"
                @click="submitDocumentRequest"
            >
                Send Request
            </DsButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
import type { Client, SendRequestPayload } from '@/company-portal/view-file/RequestDocument.types';
import { genericMultiselectPtOptions } from '@/components/Components/genericMultiSelectPassthroughOptions';
import DsButton from '@ds/form/input/DsButton.vue';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import MultiSelect from 'primevue/multiselect';
import { computed, reactive, ref } from 'vue';

const props = defineProps<{
    clients?: Client[];
    fileId?: number;
    hasError?: boolean;
}>();

const documents = reactive<string[]>(['']);
const selectedClients = ref<Client[]>([]);

const possibleClients = computed(() => {
    return (
        props.clients?.map(client => ({
            ...client,
            id: client.id,
            displayOption: `${client.role.charAt(0).toUpperCase() + client.role.slice(1)} - ${client.email}`,
        })) || []
    );
});

const sendRequestDisabled = computed(() => {
    return !selectedClients.value.length;
});

const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'sendRequest', payload: SendRequestPayload): void;
}>();

function incrementDocCount() {
    documents.push('');
}

function removeDocument(index: number) {
    if (documents.length > 1) {
        documents.splice(index, 1);
    }
}

function submitDocumentRequest() {
    const filteredDocuments = documents.filter(doc => doc.trim() !== '');
    emit('sendRequest', {
        selectedClients: selectedClients.value,
        documents: filteredDocuments,
    });
}
</script>
